'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, Award, Film, Star, ArrowLeft, CheckCircle, ChevronRight, ChevronLeft, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const categories = [
  'Short Film', 'Documentary', 'Music Video', 'Web Series Pilot',
  'Social Reels', 'Photo Essay', 'Branded Content', 'Audio Podcast',
];

const steps = ['Personal Details', 'Category & Venue', 'Confirm'];

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '', email: '', mobile: '', password: '',
    category: '', agreeTerms: false,
  });
  const { signUp } = useAuth();
  const router = useRouter();

  const handleNext = () => setStep((s) => Math.min(s + 1, 3));
  const handleBack = () => setStep((s) => Math.max(s - 1, 1));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData?.agreeTerms) return;
    setLoading(true);
    try {
      await signUp(formData?.email, formData?.password, {
        fullName: formData?.fullName,
      });
      toast.success('Account created! Welcome to Motion Max Awards.');
      setStep(3);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: Cinematic visual panel */}
      <div className="hidden lg:flex lg:w-5/12 relative overflow-hidden flex-col">
        <Image
          src="https://img.rocket.new/generatedImages/rocket_gen_img_19b4b25b2-1773109519520.png"
          alt="Professional filmmaker operating cinema camera on a film production set"
          fill
          priority
          className="object-cover"
          sizes="42vw"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/60 to-black/80" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 60% 40%, rgba(201,168,76,0.12) 0%, transparent 60%)' }} />

        <div className="relative z-10 flex flex-col justify-between p-10 h-full">
          <Link href="/" className="flex items-center gap-3 group">
            <ArrowLeft size={16} className="text-white/50 group-hover:text-primary transition-colors" />
            <div className="w-10 h-10 rounded-xl gradient-gold flex items-center justify-center font-extrabold text-primary-foreground text-lg">M</div>
            <span className="font-extrabold text-xl text-white">Motion Max Awards</span>
          </Link>

          <div>
            <h2 className="text-3xl font-extrabold text-white mb-4" style={{ lineHeight: 1.2 }}>
              Join India&apos;s Most Celebrated <span className="gradient-gold-text">Media Awards</span>
            </h2>
            <p className="text-white/60 text-sm leading-relaxed mb-8">
              Register in minutes. Submit your best work. Win recognition, prizes, and a place in India&apos;s creator hall of fame.
            </p>

            <div className="space-y-3">
              {[
                { icon: <Film size={16} />, text: '18 award categories to enter' },
                { icon: <Award size={16} />, text: '₹5 Lakh+ in total prizes' },
                { icon: <Star size={16} />, text: 'Judged by 50+ industry experts' },
                { icon: <CheckCircle size={16} />, text: 'Certificate for every participant' },
              ]?.map((item) => (
                <div key={item?.text} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-primary/20 flex items-center justify-center text-primary flex-shrink-0">
                    {item?.icon}
                  </div>
                  <span className="text-white/80 text-sm">{item?.text}</span>
                </div>
              ))}
            </div>

            <p className="text-white/40 text-xs mt-8">Season 3 · Deadline: 31 August 2026</p>
          </div>
        </div>
      </div>

      {/* Right: Registration form */}
      <div className="w-full lg:w-7/12 flex items-center justify-center px-6 py-12 bg-background overflow-y-auto">
        <div className="w-full max-w-lg">
          {/* Mobile logo */}
          <div className="flex items-center justify-between mb-8 lg:hidden">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl gradient-gold flex items-center justify-center font-extrabold text-primary-foreground">M</div>
              <span className="font-extrabold text-lg gradient-gold-text">Motion Max Awards</span>
            </div>
            <Link href="/" className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
              <ArrowLeft size={12} /> Back
            </Link>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-8">
            {steps?.map((label, i) => (
              <React.Fragment key={label}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    step > i + 1 ? 'bg-success text-white' : step === i + 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    {step > i + 1 ? <CheckCircle size={14} /> : i + 1}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${step === i + 1 ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
                </div>
                {i < steps?.length - 1 && <div className={`flex-1 h-px ${step > i + 1 ? 'bg-success' : 'bg-muted'}`} />}
              </React.Fragment>
            ))}
          </div>

          {step === 1 && (
            <div>
              <h2 className="text-2xl font-extrabold text-foreground mb-1" style={{ letterSpacing: '-0.025em' }}>Create Your Account</h2>
              <p className="text-sm text-muted-foreground mb-7">
                Already registered?{' '}
                <Link href="/login" className="text-primary hover:text-primary-light font-semibold">Sign in</Link>
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Full Name</label>
                  <input
                    type="text"
                    value={formData?.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className="input-dark w-full px-4 py-3.5 rounded-xl text-sm"
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Email Address</label>
                  <input
                    type="email"
                    value={formData?.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-dark w-full px-4 py-3.5 rounded-xl text-sm"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Mobile Number</label>
                  <input
                    type="tel"
                    value={formData?.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    className="input-dark w-full px-4 py-3.5 rounded-xl text-sm"
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData?.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="input-dark w-full px-4 py-3.5 pr-12 rounded-xl text-sm"
                      placeholder="Min. 8 characters"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleNext}
                  disabled={!formData?.fullName || !formData?.email || !formData?.password}
                  className="btn-primary w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2 text-sm mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Continue <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="text-2xl font-extrabold text-foreground mb-1" style={{ letterSpacing: '-0.025em' }}>Choose Your Category</h2>
              <p className="text-sm text-muted-foreground mb-7">Select the category you want to enter. You can add more categories later.</p>
              <div className="grid grid-cols-2 gap-2 mb-6">
                {categories?.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setFormData({ ...formData, category: cat })}
                    className={`px-4 py-3 rounded-xl text-sm font-medium text-left transition-all ${
                      formData?.category === cat
                        ? 'bg-primary text-primary-foreground'
                        : 'glass-card text-muted-foreground hover:text-primary hover:border-primary/40'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="flex items-start gap-3 mb-6">
                <input
                  type="checkbox"
                  id="terms"
                  checked={formData?.agreeTerms}
                  onChange={(e) => setFormData({ ...formData, agreeTerms: e.target.checked })}
                  className="w-4 h-4 rounded accent-primary mt-0.5"
                />
                <label htmlFor="terms" className="text-xs text-muted-foreground cursor-pointer leading-relaxed">
                  I agree to the{' '}
                  <Link href="/terms" className="text-primary hover:underline">Terms & Conditions</Link>,{' '}
                  <Link href="/rules" className="text-primary hover:underline">Competition Rules</Link>, and{' '}
                  <Link href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
                </label>
              </div>
              <div className="flex gap-3">
                <button onClick={handleBack} className="btn-outline-gold flex-1 py-4 rounded-xl font-semibold flex items-center justify-center gap-2 text-sm">
                  <ChevronLeft size={16} /> Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !formData?.agreeTerms}
                  className="btn-primary flex-1 py-4 rounded-xl font-bold flex items-center justify-center gap-2 text-sm disabled:opacity-60"
                >
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Creating...</> : <><Award size={16} /> Create Account</>}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-8">
              <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-success" />
              </div>
              <h2 className="text-2xl font-extrabold text-foreground mb-3" style={{ letterSpacing: '-0.025em' }}>Account Created!</h2>
              <p className="text-muted-foreground mb-2">Welcome to Motion Max Media Awards, {formData?.fullName || 'Creator'}!</p>
              <p className="text-sm text-muted-foreground mb-8">Your account is ready. Sign in to start your submission journey.</p>
              <div className="space-y-3">
                <Link href="/login" className="btn-primary w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2 text-sm">
                  <LogIn size={16} /> Sign In to Your Account
                </Link>
                <Link href="/" className="btn-outline-gold w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 text-sm">
                  Back to Home
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
